/**
 * Timezone-aware time helpers built on Intl (no extra dependency).
 * All "date keys" are YYYY-MM-DD strings interpreted in a given IANA timezone.
 */

type DateInput = Date | string | number

const MINUTE_MS = 60_000
const HOUR_MINUTES = 60
const DAY_MINUTES = 24 * HOUR_MINUTES

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function getFormatter(key: string, factory: () => Intl.DateTimeFormat) {
  let formatter = formatterCache.get(key)
  if (!formatter) {
    formatter = factory()
    formatterCache.set(key, formatter)
  }
  return formatter
}

export function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value)
}

export function isValidDate(value: DateInput | null | undefined): boolean {
  if (value == null || value === '') return false
  return !Number.isNaN(toDate(value).getTime())
}

function getZonedParts(value: DateInput, timeZone: string) {
  const formatter = getFormatter(`parts|${timeZone}`, () => new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }))
  const parts: Record<string, number> = {}
  formatter.formatToParts(toDate(value)).forEach((part) => {
    if (part.type !== 'literal') parts[part.type] = Number(part.value)
  })
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour === 24 ? 0 : parts.hour,
    minute: parts.minute,
    second: parts.second,
  }
}

const pad = (value: number) => String(value).padStart(2, '0')

/** Calendar date (YYYY-MM-DD) of an instant in the given timezone. */
export function toDateKey(value: DateInput, timeZone: string): string {
  const { year, month, day } = getZonedParts(value, timeZone)
  return `${year}-${pad(month)}-${pad(day)}`
}

/** Minutes since local midnight in the given timezone. */
export function getMinutesOfDay(value: DateInput, timeZone: string): number {
  const { hour, minute, second } = getZonedParts(value, timeZone)
  return hour * HOUR_MINUTES + minute + second / 60
}

/** Offset of the timezone from UTC in minutes at the given instant (e.g. BST = +60). */
export function getTimeZoneOffsetMinutes(value: DateInput, timeZone: string): number {
  const date = toDate(value)
  const { year, month, day, hour, minute, second } = getZonedParts(date, timeZone)
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second)
  return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / MINUTE_MS)
}

/** Converts a wall-clock time on a date in `timeZone` to the UTC instant. */
export function zonedTimeToUtc(dateKey: string, minutesOfDay: number, timeZone: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number)
  const naiveUtc = Date.UTC(year, month - 1, day, 0, 0, 0) + minutesOfDay * MINUTE_MS
  // Two passes settle DST transitions.
  let offset = getTimeZoneOffsetMinutes(naiveUtc, timeZone)
  let result = naiveUtc - offset * MINUTE_MS
  const secondOffset = getTimeZoneOffsetMinutes(result, timeZone)
  if (secondOffset !== offset) {
    offset = secondOffset
    result = naiveUtc - offset * MINUTE_MS
  }
  return new Date(result)
}

export function addDaysToDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + days))
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`
}

/** Whole days from `from` to `to` (both YYYY-MM-DD). */
export function diffDateKeysInDays(to: string, from: string): number {
  const toUtc = Date.parse(`${to}T00:00:00Z`)
  const fromUtc = Date.parse(`${from}T00:00:00Z`)
  return Math.round((toUtc - fromUtc) / (DAY_MINUTES * MINUTE_MS))
}

/** Short timezone name such as "BST", "GMT" or "GMT+5:30". */
export function getTimeZoneAbbreviation(timeZone: string, at: DateInput = new Date(), locale = 'en-GB'): string {
  const formatter = getFormatter(`abbr|${locale}|${timeZone}`, () => new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: 'short',
  }))
  return formatter.formatToParts(toDate(at)).find((part) => part.type === 'timeZoneName')?.value ?? timeZone
}

export function formatTimeInZone(value: DateInput, timeZone: string, locale = 'en-GB'): string {
  if (!isValidDate(value)) return '--:--'
  const formatter = getFormatter(`time|${locale}|${timeZone}`, () => new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }))
  return formatter.format(toDate(value))
}

export function formatTimeRangeInZone(start: DateInput, end: DateInput, timeZone: string, locale = 'en-GB'): string {
  return `${formatTimeInZone(start, timeZone, locale)}–${formatTimeInZone(end, timeZone, locale)}`
}

/** "Tue, 30 Sep" for a YYYY-MM-DD date key. */
export function formatDateKeyLabel(dateKey: string, locale = 'en-GB'): string {
  const formatter = getFormatter(`datekey|${locale}`, () => new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }))
  return formatter.format(new Date(`${dateKey}T12:00:00Z`))
}

/** "25 min", "1 h", "2 h 5 min", "3 d". */
export function formatDuration(totalMinutes: number): string {
  const minutes = Math.max(0, Math.round(totalMinutes))
  if (minutes < HOUR_MINUTES) return `${minutes} min`
  if (minutes < DAY_MINUTES) {
    const hours = Math.floor(minutes / HOUR_MINUTES)
    const rest = minutes % HOUR_MINUTES
    return rest ? `${hours} h ${rest} min` : `${hours} h`
  }
  return `${Math.floor(minutes / DAY_MINUTES)} d`
}

/** "in 25 min", "12 min ago", "just now". */
export function formatRelativeTime(target: DateInput, now: DateInput = Date.now()): string {
  if (!isValidDate(target)) return ''
  const diffMs = toDate(target).getTime() - toDate(now).getTime()
  if (Math.abs(diffMs) < MINUTE_MS) return 'just now'
  const label = formatDuration(Math.abs(diffMs) / MINUTE_MS)
  return diffMs > 0 ? `in ${label}` : `${label} ago`
}

/** "12 min late"; empty string when not late. */
export function formatLateBy(scheduledStart: DateInput, now: DateInput = Date.now()): string {
  if (!isValidDate(scheduledStart)) return ''
  const lateMinutes = (toDate(now).getTime() - toDate(scheduledStart).getTime()) / MINUTE_MS
  if (lateMinutes < 1) return ''
  return `${formatDuration(lateMinutes)} late`
}

export function formatOverdue(days: number): string {
  return `Overdue ${Math.max(1, Math.round(days))}d`
}

/** "3s ago", "2m ago", "1h ago" — compact freshness label. */
export function formatSecondsAgo(timestamp: number | null, now: number = Date.now()): string {
  if (timestamp == null) return 'never'
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  return `${Math.floor(minutes / 60)}h ago`
}

/** Compact duration for chips: "46m", "1h 21m", "2h", "3d". */
export function formatCompactDuration(totalMinutes: number): string {
  const minutes = Math.max(0, Math.floor(totalMinutes))
  if (minutes < 60) return `${minutes}m`
  if (minutes < 24 * 60) {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest ? `${hours}h ${rest}m` : `${hours}h`
  }
  return `${Math.floor(minutes / (24 * 60))}d`
}

/** "1h 21m ago" / "just now" from an ISO timestamp; null when unknown. */
export function formatCompactAgo(value: DateInput | null | undefined, now: DateInput = Date.now()): string | null {
  if (value == null || !isValidDate(value)) return null
  const minutes = (toDate(now).getTime() - toDate(value).getTime()) / MINUTE_MS
  return minutes < 1 ? 'just now' : `${formatCompactDuration(minutes)} ago`
}
